import { trackEvent } from './analytics';

/**
 * Product event taxonomy — the single source of truth for analytics event names
 * and payloads. Call sites go through `track` so names/props cannot drift from
 * the taxonomy. Rules: snake_case props, and NEVER PII, free text, or monetary
 * amounts (counts, booleans, and enum-ish strings only).
 *
 * Naming namespaces:
 * - `action.<domain>.<verb>` — every user-initiated action. The "Ações" PostHog
 *   dashboard ranks and time-series EVERYTHING matching `action.%`, so a new
 *   action only needs a name here + a call site; dashboards pick it up.
 * - `ui.*` — micro-interactions (tab switches, filters, period controls). Kept
 *   out of `action.%` so high-frequency UI fiddling doesn't drown the feature
 *   ranking; they have dedicated insights instead.
 * - everything else (`new_home.*`, `friction.*`, `*.viewed`, `*_finished`,
 *   `*_failed`) — passive views, impressions, and system outcomes.
 */

export type WidgetId =
  | 'disponivel'
  | 'ritmo'
  | 'em-aberto'
  | 'patrimonio'
  | 'metas'
  | 'revisar'
  | 'categorias'
  | 'assinaturas'
  | 'ultimas';

export type WidgetAction =
  | 'open_link'
  | 'timeline_day'
  | 'open_goal'
  | 'create_goal'
  | 'confirm'
  | 'confirm_all'
  | 'categorize'
  | 'toggle_category'
  | 'open_history'
  | 'open_transaction'
  | 'info';

export type NavSurface = 'sidebar' | 'bottom_nav' | 'bottom_nav_drawer' | 'header_back';
export type PanoramaMethod = 'swipe' | 'dot' | 'button' | 'initial';
export type ConnectReason = 'new' | 'update' | 'reauth' | 'renew' | 'onboarding';
export type ImportStatus = 'completed' | 'timeout' | 'action_required' | 'error';
export type CategorizeSource = 'transactions' | 'review_queue' | 'home_revisar' | 'category_detail';
export type CategorizeMode = 'single' | 'batch_selected' | 'batch_same_description' | 'confirm_all';
export type ChatFailureReason =
  | 'timeout'
  | 'offline'
  | 'network'
  | 'llm'
  | 'limit_exceeded'
  | 'rate_limited'
  | 'unknown';
/** How a write the assistant proposed ended once the person answered its card (VER-555). */
export type ChatWriteStatus = 'pending' | 'confirming' | 'confirmed' | 'cancelled' | 'expired' | 'failed';
/** Where a category was created/edited/archived from. */
export type CategorySurface = 'orcamento' | 'category_filter' | 'picker';
/** Where a budget limit was saved from. */
export type LimitSurface = 'orcamento' | 'category_detail' | 'category_filter';
/** Recurring-payment decision surfaces. */
export type RecurringSource = 'grid' | 'review_sheet';
/** Where a refund action was initiated from. */
export type RefundActionSource = 'transactions' | 'detail' | 'review_queue' | 'picker';
export type RefundReviewReason = 'possible_refund' | 'refund_not_arrived';
export type RefundReviewAction = 'link' | 'not_refund' | 'keep_expense';
export type ThemeMode = 'light' | 'dark' | 'system';
/** How two positions relate once grouped — the wire roles, lowercased for analytics. */
export type DuplicateRole = 'mirror' | 'additive';
export type DuplicateDecision = DuplicateRole | 'not_duplicate';

/** Tabbed surfaces (drawers/pages) — `ui.tab_viewed`. */
export type TabSurface =
  | 'category_detail' // drawer: Resumo / Lançamentos / Limite / Regras
  | 'expenses' // tab bar: Conta / Cartão de crédito
  | 'recurring_chart' // half-year toggle: Jan–Jun / Jul–Dez
  | 'recurring_sections'; // accordion: assinaturas / fixas / parcelamentos
export type TabTrigger = 'open' | 'switch';

/** Filterable surfaces — `ui.filter_applied`. */
export type FilterSurface =
  | 'transactions'
  | 'orcamento'
  | 'investments'
  | 'category_detail'
  | 'merchant_frequency';
export type FilterMethod = 'drawer' | 'chip' | 'chart' | 'search' | 'control';

export type PeriodSurface = 'global' | 'investments_calendar' | 'transactions' | 'spaces';
export type PeriodMethod = 'preset' | 'custom_range' | 'month_step' | 'day_select';
export type PeriodPreset = 'current_month' | 'last_30' | 'last_90' | 'last_180' | 'custom';

type ProductEvents = {
  // ————— navigation / passive views / system outcomes —————
  'nav.clicked': { surface: NavSurface; target: string; scope: 'personal' | 'space' };
  'space.expense.sent': { via: 'manual' | 'partial' | 'batch'; batch_count: number; rule_created?: boolean; rule_type: string | null };
  'scope.switched': { to: 'personal' | 'space' };
  // Espaços activation funnel (VER-421): create -> invite accepted -> first auto-entry -> first settlement. Counts and
  // vocabulary only: never an amount, a name, an e-mail or an id of a person or a space.
  'space.created': { kind: 'house' | 'trip' | 'other'; members: number; model: 'split' | 'assigned' };
  'space.invite.sent': { source: 'creation' | 'settings'; role: 'editor' | 'viewer' };
  'space.invite.accepted': { surface: 'home' | 'bell' };
  'space.expense.removed': { exception: boolean };
  'space.rule.paused': { rule_type: string };
  'space.settlement.registered': { transfers: number };
  'space.partner_preview.opened': undefined;
  'new_home.viewed': { layout: 'carousel' | 'grid'; has_data: boolean };
  'new_home.widget_impression': { widget: WidgetId; position: number };
  'new_home.widget_action': { widget: WidgetId; action: WidgetAction; count?: number };
  'new_home.panorama_slide_viewed': { index: number; widget: WidgetId; method: PanoramaMethod };
  'connect.widget_error': { stage?: string };
  'connect.import_finished': { status: ImportStatus; duration_ms?: number };
  'goal.create_failed': undefined;
  'chat.response_received': { duration_ms: number };
  'chat.response_failed': { reason: ChatFailureReason };
  'chat.write_answered': { status: ChatWriteStatus };
  'billing.plans_viewed': undefined;
  'mcp.settings_viewed': undefined;
  'problem_report.submit_failed': undefined;
  'friction.error_shown': { surface: string; flow?: string };
  'friction.load_failed': { surface: string };
  'friction.retry_clicked': { surface: string };

  // ————— actions: auth / onboarding / settings —————
  'action.notifications.entry_clicked': { message_id: string };
  'action.auth.sign_up': undefined;
  'action.auth.sign_out': { surface: 'sidebar' | 'bottom_nav' | 'onboarding' };
  'action.onboarding.submit_cpf': undefined;
  'action.onboarding.whatsapp_start': undefined;
  'action.onboarding.whatsapp_resend': undefined;
  'action.onboarding.whatsapp_confirm': undefined;
  'action.onboarding.accept_consent': {
    purpose: 'terms_privacy_openfinance' | 'whatsapp_messaging';
    accepted: boolean;
    version: string;
  };
  'action.onboarding.step_completed': { step: number; deferred: boolean };
  'action.onboarding.completed': {
    connections: number;
    streams_classified: number;
  };
  'action.settings.change_theme': { mode: ThemeMode };
  'action.settings.toggle_privacy': { hidden: boolean };

  // ————— actions: bank connection —————
  'action.connect.start': { reason: ConnectReason };
  'action.connect.submit_cpf': undefined;
  'action.connect.complete': { connector_id?: number };
  'action.connect.disconnect': undefined;

  // ————— actions: categorization / review —————
  'action.categorize.apply': {
    source: CategorizeSource;
    mode: CategorizeMode;
    count: number;
    rule_created: boolean;
  };

  // ————— actions: tags & rename (VER-378 / VER-377) —————
  'action.tag.create': { via_filter: boolean };
  'action.tag.assign': {
    source: 'transactions';
    mode: 'single' | 'batch_same_description';
    count: number;
  };
  'action.transaction.rename': {
    /** Which surface the rename happened on (VER-377 is rename-everywhere). */
    source: 'transactions' | 'home' | 'orcamento' | 'recurring_history';
    mode: 'single' | 'batch_same_description';
    count: number;
    /** true = "Restaurar nome original" (the name was cleared, not set). */
    cleared: boolean;
  };

  // ————— actions: categories & rules —————
  'action.category.create': { via_suggestion: boolean; kind?: string; with_budget?: boolean };
  'action.category.update': { surface: CategorySurface };
  'action.category.archive': { surface: CategorySurface };
  'action.category.unarchive': { surface: CategorySurface };
  'action.category_rule.delete': undefined;

  // ————— actions: budget limits —————
  'action.budget.set_limit': { surface: LimitSurface; cleared: boolean };
  'action.budget.link_goal': undefined;

  // ————— actions: transactions —————
  'action.transaction.open_detail': {
    source: 'list' | 'category_detail' | 'home_ultimas' | 'orcamento' | 'recurring_history';
  };
  'action.transaction.mark_recurring': { cadence?: string; kind?: string };
  'action.transaction.copy': { target: 'boleto_line' | 'detail_field' };

  // ————— actions: linked refunds —————
  'action.refund.link': { source: RefundActionSource; partial: boolean };
  'action.refund.unlink': { source: RefundActionSource; undo: boolean };
  'action.refund.flag_expected': { source: RefundActionSource; flagged: boolean; undo: boolean };
  'action.refund.review_resolve': { reason: RefundReviewReason; action: RefundReviewAction };

  // ————— actions: goals —————
  'action.goal.create_start': undefined;
  'action.goal.create': { type: string; linked_entities_count: number };
  'action.goal.update': { via: 'simulator' | 'edit' };
  'action.goal.delete': undefined;
  'action.goal.open_simulator': undefined;

  // ————— actions: recurring payments —————
  'action.recurring.confirm': { source: RecurringSource };
  'action.recurring.reject': { source: RecurringSource };
  'action.recurring.cancel': { source: RecurringSource };
  'action.recurring.reactivate': { source: RecurringSource };
  'action.recurring.delete': undefined;
  'action.recurring.sync': undefined;
  'action.recurring.open_history': { source: 'page' | 'home_widget' };

  // ————— actions: chat (Link) —————
  'action.chat.open': { page?: string };
  'action.chat.send_message': { page?: string; message_index: number };
  'action.chat.retry': undefined;
  'action.chat.clear_conversation': undefined;
  'action.chat.forget_memory': undefined;
  'action.chat.answer_write': { confirmed: boolean };

  // ————— actions: billing —————
  'action.billing.checkout_start': { plan_code: string };
  'action.billing.checkout_complete': { plan_code?: string };
  'action.billing.open_portal': undefined;
  'action.billing.open_invoice': undefined;
  'action.mcp.copy_url': undefined;

  // ————— actions: detail opens (feature engagement) —————
  'action.fatura.open_detail': { source: 'invoices_page' | 'em_aberto_widget' | 'transactions' };
  'action.investment.open_detail': { source: 'list' | 'calendar' };
  'action.investment.open_calendar': undefined;
  /** Duplicate-position review: what the user decided, and whether it matched the
   * sync's suggestion (the signal that says whether the detection is trustworthy). */
  'action.investment.duplicate_reviewed': {
    decision: DuplicateDecision;
    suggested_role: DuplicateRole;
    followed_suggestion: boolean;
    /** Stable backend token naming the deciding evidence — never free text. */
    match_reason?: string;
  };
  'action.investment.unmerge': { role: DuplicateRole };
  'action.investment.merge': { role: DuplicateRole; count: number };

  // ————— actions: feedback —————
  'action.problem_report.open': { source?: string };
  'action.problem_report.submit': undefined;

  // ————— passive: investments (VER-256 liquidity rollout gauge) —————
  /** Whole-portfolio availability distribution, emitted once per investments-page
   * load whatever the flag state — it measures the real Indeterminado /
   * settlement-unknown rates that decide the flag flip. Shares (of balance,
   * 0–1) and counts only, never amounts. */
  'investments.liquidity_distribution': {
    daily_share: number;
    short_share: number;
    long_share: number;
    unknown_share: number;
    daily_count: number;
    short_count: number;
    long_count: number;
    unknown_count: number;
    total_count: number;
    settlement_unknown_count: number;
    partial_availability_count: number;
    blocked_contradiction_count: number;
  };

  // ————— ui micro-interactions (own insights, excluded from action ranking) —————
  'ui.notifications.bell_opened': { unread: number };
  'ui.tab_viewed': { surface: TabSurface; tab: string; trigger: TabTrigger };
  'ui.filter_applied': {
    surface: FilterSurface;
    filter: string;
    action: 'set' | 'clear';
    method?: FilterMethod;
    /** Only enum-ish values (sort keys, payment method, kind) — never ids or free text. */
    value?: string;
  };
  'ui.period_changed': {
    surface: PeriodSurface;
    method: PeriodMethod;
    preset?: PeriodPreset;
    /** Route where the change happened (e.g. '/transactions') — helps judge per-page usage. */
    page?: string;
  };
};

export type ProductEventName = keyof ProductEvents;

export function track<Name extends ProductEventName>(
  name: Name,
  ...payload: ProductEvents[Name] extends undefined ? [] : [ProductEvents[Name]]
): void {
  trackEvent(name, payload[0]);
}
